import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  Group,
  Button,
  PageHeader,
  ResourceLayout,
  BottomDrawer,
  FormField,
  TextInput,
  Select,
  Alert,
  Paper,
  Text,
  Badge,
  MobileListRow,
  MultiSelect,
  PhoneInput,
  RowActions,
  ConfirmDialog,
  detectDefaultPhoneCountry,
  isValidNationalPhone,
  parseStoredPhone,
  type CountryCode,
  type ResourceQuery,
} from '@vipso/shared';
import {
  useListStaffQuery,
  useAddUpdateStaffMutation,
  useDeleteStaffMutation,
  useListRolesQuery,
} from '../api/tenantApi';
import { useListWarehousesQuery } from '../api/enhanceGenerated';
import { useModuleAccess } from '../hooks/useModuleAccess';

type StaffRow = {
  id: string;
  userId: string;
  roleId?: string | null;
  isActive: boolean;
  warehouseIds?: string[] | null;
  user?: {
    id?: string;
    name?: string;
    phone?: string;
    email?: string;
    countryCode?: string | null;
  } | null;
  role?: { id?: string; name?: string } | null;
  warehouses?: Array<{ id: string; name: string }> | null;
};

export function StaffPage() {
  const { t } = useTranslation();
  const access = useModuleAccess('staff');
  const canEdit = access.canUpdate;
  const canAdd = access.canCreate;
  const canRemove = access.canDelete;
  const [query, setQuery] = useState<ResourceQuery>({
    page: 1,
    pageLimit: 20,
    search: '',
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<StaffRow | null>(null);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    phoneCountry: detectDefaultPhoneCountry() as string,
    email: '',
    roleId: '',
    warehouseIds: [] as string[],
  });
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<StaffRow | null>(null);

  const { data, isLoading, isFetching, isError, refetch } = useListStaffQuery({
    page: query.page,
    pageLimit: query.pageLimit,
    search: query.search || undefined,
  });
  const { data: rolesData } = useListRolesQuery();
  const { data: warehousesData } = useListWarehousesQuery({ pageLimit: 100 });
  const [save, { isLoading: saving }] = useAddUpdateStaffMutation();
  const [remove, { isLoading: deleting }] = useDeleteStaffMutation();

  const list = data?.listStaff;
  const roleOptions = (rolesData?.listRoles ?? []).map((r) => ({
    value: r.id,
    label: r.name,
  }));
  const warehouseOptions = (warehousesData?.listWarehouses?.rows ?? []).map((w) => ({
    value: w.id,
    label: w.name,
  }));

  function openCreate() {
    setEditing(null);
    setPhoneError(null);
    setForm({
      name: '',
      phone: '',
      phoneCountry: detectDefaultPhoneCountry(),
      email: '',
      roleId: '',
      warehouseIds: [],
    });
    setModalOpen(true);
  }

  function openEdit(row: StaffRow) {
    setEditing(row);
    setPhoneError(null);
    const parsed = parseStoredPhone(row.user?.phone, row.user?.countryCode);
    setForm({
      name: row.user?.name || '',
      phone: parsed.phone,
      phoneCountry: parsed.country,
      email: row.user?.email || '',
      roleId: row.roleId || row.role?.id || '',
      warehouseIds: row.warehouseIds ?? [],
    });
    setModalOpen(true);
  }

  async function onSave() {
    if (!editing) {
      if (!isValidNationalPhone(form.phone, form.phoneCountry)) {
        setPhoneError(t('phone.invalid'));
        return;
      }
    }
    setPhoneError(null);
    await save({
      ...(editing ? { id: editing.id } : {}),
      name: form.name.trim(),
      phone: form.phone.trim() || undefined,
      countryCode: form.phoneCountry as CountryCode,
      email: form.email.trim() || undefined,
      roleId: form.roleId || undefined,
      warehouseIds: form.warehouseIds.length ? form.warehouseIds.map(Number) : undefined,
    }).unwrap();
    setModalOpen(false);
  }

  async function handleDeactivate() {
    if (!confirmDelete) return;
    await remove(confirmDelete.id).unwrap();
    setConfirmDelete(null);
    setModalOpen(false);
  }

  return (
    <Stack gap="md">
      <PageHeader
        title={t('nav.staff')}
        description={t('staff.description')}
      />
      {isError ? <Alert color="red" title={t('staff.loadFailed')} /> : null}
      <ResourceLayout
        data={list}
        isLoading={isLoading}
        isFetching={isFetching}
        onQueryChange={setQuery}
        getRowId={(row) => row.id}
        searchable
        searchPlaceholder={t('staff.searchPlaceholder')}
        emptyTitle={t('staff.emptyTitle')}
        emptyDescription={t('staff.emptyDescription')}
        onRefresh={() => void refetch()}
        refreshLabel={t('common.refresh')}
        onRowClick={canEdit ? openEdit : undefined}
        mobileView="list"
        rowActionsHeader={t('common.actions')}
        renderRowActions={
          canEdit || canRemove
            ? (row) => (
                <RowActions
                  editLabel={t('common.edit')}
                  deleteLabel={t('staff.deactivate')}
                  onEdit={canEdit ? () => openEdit(row) : undefined}
                  onDelete={canRemove ? () => setConfirmDelete(row) : undefined}
                />
              )
            : undefined
        }
        addButton={canAdd ? <Button onClick={openCreate}>{t('staff.add')}</Button> : undefined}
        columns={[
          {
            key: 'name',
            header: t('common.name'),
            render: (row) => row.user?.name || t('common.emptyValue'),
          },
          {
            key: 'phone',
            header: t('common.phone'),
            render: (row) => row.user?.phone || t('common.emptyValue'),
          },
          {
            key: 'role',
            header: t('staff.role'),
            render: (row) => row.role?.name || t('common.emptyValue'),
          },
          {
            key: 'warehouses',
            header: t('staff.warehouses'),
            render: (row) => {
              const whs = (row as StaffRow).warehouses ?? [];
              if (!whs.length) return <Text c="dimmed">{t('common.emptyValue')}</Text>;
              return (
                <Group gap={4} wrap="wrap">
                  {whs.map((w: { id: string; name: string }) => (
                    <Badge key={w.id} size="xs" variant="light">
                      {w.name}
                    </Badge>
                  ))}
                </Group>
              );
            },
          },
          {
            key: 'status',
            header: t('common.status'),
            render: (row) => (
              <Badge color={row.isActive ? 'green' : 'gray'}>
                {row.isActive ? t('common.active') : t('common.inactive')}
              </Badge>
            ),
          },
        ]}
        renderCard={(row: StaffRow) => (
          <Paper p="md" withBorder radius="md">
            <Stack gap={4}>
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <Stack gap={4} style={{ minWidth: 0 }}>
                  <Group gap="sm">
                    <Text fw={600}>{row.user?.name || t('staff.unnamed')}</Text>
                    <Badge color={row.isActive ? 'green' : 'gray'}>
                      {row.isActive ? t('common.active') : t('common.inactive')}
                    </Badge>
                  </Group>
                  <Text size="sm" c="dimmed">
                    {row.user?.phone || t('common.noPhone')} · {row.role?.name || t('staff.noRole')}
                  </Text>
                </Stack>
                {canEdit || canRemove ? (
                  <RowActions
                    editLabel={t('common.edit')}
                    deleteLabel={t('staff.deactivate')}
                    onEdit={canEdit ? () => openEdit(row) : undefined}
                    onDelete={canRemove ? () => setConfirmDelete(row) : undefined}
                  />
                ) : null}
              </Group>
            </Stack>
          </Paper>
        )}
        renderListItem={(row: StaffRow) => (
          <MobileListRow
            title={row.user?.name || t('staff.unnamed')}
            subtitle={row.user?.phone || t('common.noPhone')}
            onClick={canEdit ? () => openEdit(row) : undefined}
            trailing={
              canEdit || canRemove ? (
                <RowActions
                  editLabel={t('common.edit')}
                  deleteLabel={t('staff.deactivate')}
                  onEdit={canEdit ? () => openEdit(row) : undefined}
                  onDelete={canRemove ? () => setConfirmDelete(row) : undefined}
                />
              ) : undefined
            }
            showChevron={!canEdit}
          />
        )}
      />
      <BottomDrawer
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? t('staff.edit') : t('staff.add')}
      >
        <Stack gap="md">
          <FormField label={t('common.name')} required>
            <TextInput
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.currentTarget.value })}
            />
          </FormField>
          <FormField label={t('common.phone')} required={!editing}>
            <PhoneInput
              value={form.phone}
              country={form.phoneCountry}
              disabled={Boolean(editing)}
              error={phoneError ?? undefined}
              countrySearchPlaceholder={t('phone.searchCountry')}
              onChange={({ phone, country }) => {
                setPhoneError(null);
                setForm({ ...form, phone, phoneCountry: country });
              }}
            />
          </FormField>
          <FormField label={t('common.email')}>
            <TextInput
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.currentTarget.value })}
            />
          </FormField>
          <FormField label={t('staff.role')}>
            <Select
              clearable
              placeholder={t('common.optional')}
              value={form.roleId || null}
              onChange={(value) => setForm({ ...form, roleId: value || '' })}
              data={roleOptions}
            />
          </FormField>
          <FormField label={t('staff.warehouses')}>
            <MultiSelect
              searchable
              clearable
              placeholder={t('staff.warehousesPlaceholder')}
              value={form.warehouseIds}
              onChange={(v) => setForm({ ...form, warehouseIds: v })}
              data={warehouseOptions}
            />
          </FormField>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              loading={saving}
              disabled={
                !form.name.trim() ||
                (!editing && !isValidNationalPhone(form.phone, form.phoneCountry)) ||
                (editing ? !canEdit : !canAdd)
              }
              onClick={() => void onSave()}
            >
              {t('common.save')}
            </Button>
          </Group>
        </Stack>
      </BottomDrawer>

      <ConfirmDialog
        opened={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => void handleDeactivate()}
        title={t('staff.deactivate')}
        message={t('common.deleteMessage', {
          name: confirmDelete?.user?.name || t('staff.unnamed'),
        })}
        confirmLabel={t('staff.deactivate')}
        loading={deleting}
        danger
      />
    </Stack>
  );
}
